/**
 * Created on Fri Jun 12 2026 4:08:23 PM
 *
 * *************************************************************************************
 * Copyright (c) AVPS Technologies Inc 2026
 * All Rights Reserved
 *
 * Unpublished rights are reserved under the copyright laws of the United States.
 * The software contained in this media is proprietary to and embodies confidential
 * the technology of AVPS Technologies Inc. Possession, use, duplication or
 * dissemination of the software and media is authorized only pursuant to a valid
 * written license from AVPS Technologies Inc.
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * RESTRICTED RIGHTS LEGEND Use, duplication, or disclosure by the U.S. Government
 * is subject to restrictions as set forth in Subparagraph (c) (1) (ii) of DFARS
 * 252.227-7013 or in FAR 52.227-19, as applicable.
 ************************************************************************************/


export const SECTION_THEME_BINDINGS = [
  { sectionKey: 'backgroundColor', themeKey: 'background', label: 'Background' },
  { sectionKey: 'color', themeKey: 'color', label: 'Text' },
  { sectionKey: 'accentColor', themeKey: 'primary', label: 'Accent' },
  { sectionKey: 'secondaryColor', themeKey: 'secondary', label: 'Secondary' },
];

export const DOMAIN_PRIMARY_DEFAULTS = {
  EDUCATION: ['#3B9394', '#23196E', '#2563eb', '#008060'],
  WAAS: ['#07111f', '#0b0f16', '#c9a46a'],
  RETAIL: ['#0891b2', '#B88E2F', '#232f3e'],
  FLEX_OFFICE: ['#2563eb', '#1d4ed8', '#0f172a'],
};

export const DOMAIN_SECONDARY_DEFAULTS = {
  EDUCATION: ['#746EAC', '#87927c', '#746eac'],
  WAAS: ['#d8c2b6', '#5f6670'],
  RETAIL: ['#232f3e', '#6b7280'],
  FLEX_OFFICE: ['#0f172a', '#64748b'],
};

const normalizeDomain = (domainType) => String(domainType || 'EDUCATION').toUpperCase().replace(/[\s-]+/g, '_');

export const linkedDefaultsFor = (domainType, field) => {
  const domain = normalizeDomain(domainType);
  if (field === 'primary' || field === 'accentColor') {
    return DOMAIN_PRIMARY_DEFAULTS[domain] || DOMAIN_PRIMARY_DEFAULTS.EDUCATION;
  }
  if (field === 'secondary' || field === 'secondaryColor') {
    return DOMAIN_SECONDARY_DEFAULTS[domain] || DOMAIN_SECONDARY_DEFAULTS.EDUCATION;
  }
  if (field === 'background' || field === 'backgroundColor') {
    return ['#ffffff', '#f8fafc', '#f4f5f6', '#e6e8eb'];
  }
  if (field === 'color') {
    return ['#111827', '#1f2937', '#1e293b', '#0b0f16', '#3e342e'];
  }
  return [];
};

export const isThemeLinkedValue = (sectionValue, themeValue, linkedDefaults = []) => {
  if (!sectionValue) return true;
  if (themeValue && sectionValue.toLowerCase?.() === themeValue.toLowerCase?.()) return true;
  return linkedDefaults.some((item) => String(item).toLowerCase() === String(sectionValue).toLowerCase());
};

export const effectiveSectionColor = (sectionValue, themeValue, linkedDefaults = [], fallback = '') => (
  isThemeLinkedValue(sectionValue, themeValue, linkedDefaults) ? (themeValue || fallback) : (sectionValue || themeValue || fallback)
);

export const resolveThemeLinkedSectionStyle = (section = {}, theme = {}, domainType = 'EDUCATION') => {
  const style = { ...(section.style || {}) };
  const overrides = style.themeOverrides || {};

  SECTION_THEME_BINDINGS.forEach((binding) => {
    if (overrides[binding.sectionKey]) return;
    const linked = linkedDefaultsFor(domainType, binding.themeKey);
    const fallback = binding.themeKey === 'background'
      ? '#ffffff'
      : binding.themeKey === 'color'
        ? '#111827'
        : '';
    const resolvedValue = effectiveSectionColor(
      style[binding.sectionKey],
      theme[binding.themeKey],
      linked,
      fallback
    );
    if (resolvedValue) {
      style[binding.sectionKey] = resolvedValue;
    } else {
      delete style[binding.sectionKey];
    }
  });

  return { ...section, style };
};

export default resolveThemeLinkedSectionStyle;
